import React, { useId, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { pageAPI } from "../../services/api";
import {
  FiFacebook,
  FiInstagram,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiExternalLink
} from "react-icons/fi";

// ✅ Icône TikTok (simple SVG) - pas besoin de lib externe
const TikTokIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.5 3c.6 2.9 2.7 4.9 5.5 5.1v3.2c-1.9.1-3.7-.5-5.2-1.6V17c0 3.4-2.8 6-6.2 6-3.4 0-6.2-2.7-6.2-6 0-3.4 2.8-6 6.2-6 .4 0 .9 0 1.3.1v3.5c-.4-.2-.8-.3-1.3-.3-1.5 0-2.7 1.2-2.7 2.7s1.2 2.7 2.7 2.7 2.7-1.2 2.7-2.7V3h3.2Z" />
  </svg>
);

const FlowerSVG = ({ idPrefix, className = "absolute", style = {} }) => {
  const fg1 = `fg1-${idPrefix}`;
  const fg2 = `fg2-${idPrefix}`;

  return (
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={fg1} x1="0" x2="1">
          <stop offset="0" stopColor="#FCE7F3" />
          <stop offset="1" stopColor="#E11D79" />
        </linearGradient>
        <linearGradient id={fg2} x1="0" x2="1">
          <stop offset="0" stopColor="#F59ACB" />
          <stop offset="1" stopColor="#B0125A" />
        </linearGradient>
      </defs>

      <g transform="translate(20,20)" fill={`url(#${fg1})`} opacity="0.16">
        <ellipse cx="40" cy="40" rx="34" ry="18" />
        <ellipse cx="70" cy="20" rx="18" ry="34" transform="rotate(45 70 20)" />
        <circle cx="55" cy="55" r="10" fill={`url(#${fg2})`} opacity="0.9" />
      </g>

      <g transform="translate(110,120)" fill={`url(#${fg2})`} opacity="0.14">
        <circle cx="10" cy="10" r="8" />
        <path
          d="M10 0 L12 6 L18 6 L13 9 L15 15 L10 11 L5 15 L7 9 L2 6 L8 6 Z"
          opacity="0.95"
        />
      </g>
    </svg>
  );
};

const Footer = () => {
  const { t, lang } = useLanguage();
  const id = useId();

  // Pages CMS à afficher dans le footer (gérées depuis l'admin)
  const [cmsPages, setCmsPages] = useState([]);
  useEffect(() => {
    let alive = true;
    pageAPI.menu()
      .then((res) => {
        const data = (res && res.data && res.data.data) || [];
        if (alive) setCmsPages(data.filter((p) => p.showInFooter));
      })
      .catch(() => { if (alive) setCmsPages([]); });
    return () => { alive = false; };
  }, []);

  const cmsHref = (p) => (p.slug.startsWith("cgu/") ? `/${p.slug}` : `/p/${p.slug}`);

  // ✅ Liens officiels (en dur)
  const social = {
    facebook: "https://www.facebook.com/Maxula.ma/",
    instagram: "https://www.instagram.com/maxula.ma/",
    tiktok: "https://www.tiktok.com/@maxula.ma"
  };

  const contact = {
    address: "Avenue Beethoven, Résidence Oumnia 1, Bureau 83, Tanger – Maroc",
    phone1: "0539 34 24 94",
    phone2: "0606 06 61 62",
    email: "contact@maxula.ma",
    hours: t('footer.hours')
  };

  const openExternal = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const year = new Date().getFullYear();

  const logoSrc = `${process.env.PUBLIC_URL || ""}/logo/maxula-white.png`;
  const fallbackLogo = `${process.env.PUBLIC_URL || ""}/maxula-white.png`;

  return (
    <footer className="relative overflow-hidden bg-gradient-to-r from-primary-600 to-primary-700 text-white">
      {/* BACKGROUND DECOR */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <FlowerSVG
          idPrefix={`${id}-1`}
          className="absolute -top-24 -left-24 w-[360px] h-[360px] opacity-60"
          style={{ transform: "rotate(-10deg)" }}
        />
        <FlowerSVG
          idPrefix={`${id}-2`}
          className="absolute -bottom-28 -right-24 w-[420px] h-[420px] opacity-60"
          style={{ transform: "rotate(18deg)" }}
        />

        <svg
          className="absolute left-1/2 transform -translate-x-1/2 bottom-0 w-[560px] h-[220px] opacity-10"
          viewBox="0 0 560 220"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id={`gfooter-${id}`} x1="0" x2="1">
              <stop offset="0" stopColor="#E11D79" />
              <stop offset="1" stopColor="#7a1147" />
            </linearGradient>
          </defs>
          <ellipse
            cx="280"
            cy="120"
            rx="290"
            ry="90"
            fill={`url(#gfooter-${id})`}
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* BRAND */}
          <div className="md:col-span-4">
            <img
              src={logoSrc}
              alt="Maxula"
              className="h-100 md:h-16 lg:h-30 w-auto mb-100"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = fallbackLogo;
              }}
            />

            <p className="mt-3 text-white/90 leading-relaxed">
              {t('footer.tagline')}
              <span className="block mt-2 text-white/80">
                {t('footer.since')}
              </span>
            </p>

            {/* SOCIAL */}
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={() => openExternal(social.facebook)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/15 transition"
                aria-label="Facebook"
                title="Ouvrir Facebook"
              >
                <FiFacebook size={20} />
              </button>

              <button
                type="button"
                onClick={() => openExternal(social.instagram)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/15 transition"
                aria-label="Instagram"
                title="Ouvrir Instagram"
              >
                <FiInstagram size={20} />
              </button>

              <button
                type="button"
                onClick={() => openExternal(social.tiktok)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/15 transition"
                aria-label="TikTok"
                title="Ouvrir TikTok"
              >
                <TikTokIcon size={20} />
              </button>
            </div>
          </div>

          {/* POLES */}
          <div className="md:col-span-2">
            <h4 className="text-lg font-semibold mb-4">{t('footer.poles')}</h4>
            <ul className="space-y-2 text-white/85">
              <li>
                <Link
                  to="/experiences"
                  className="hover:text-white transition inline-flex items-center gap-2"
                >
                  Maxula Experiences <FiExternalLink className="opacity-80" />
                </Link>
              </li>
              <li>
                <Link
                  to="/laser-clinic"
                  className="hover:text-white transition inline-flex items-center gap-2"
                >
                  Maxula Laser Clinic <FiExternalLink className="opacity-80" />
                </Link>
              </li>
              <li>
                <Link
                  to="/house"
                  className="hover:text-white transition inline-flex items-center gap-2"
                >
                  Maxula House <FiExternalLink className="opacity-80" />
                </Link>
              </li>
              <li className="pt-2">
                <Link
                  to="/deals"
                  className="inline-flex items-center justify-center w-full px-3 py-2 rounded-xl
                             bg-white text-[#e11d7b] font-semibold shadow hover:shadow-lg transition"
                >
                  {t('footer.exploreDeals')}
                </Link>
              </li>
            </ul>
          </div>

          {/* ENTREPRISE */}
          <div className="md:col-span-3">
            <h4 className="text-lg font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2 text-white/85">
              <li>
                <Link to="/about" className="hover:text-white transition">
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link to="/partner" className="hover:text-white transition">
                  {t('footer.becomePartner')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition">
                  {t('footer.contactLink')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-white transition">
                  {t('footer.faq')}
                </Link>
              </li>
              {cmsPages.map((p) => (
                <li key={p.slug}>
                  <Link to={cmsHref(p)} className="hover:text-white transition">
                    {lang === 'ar' && p.titleAr ? p.titleAr : p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT */}
          <div className="md:col-span-3">
            <h4 className="text-lg font-semibold mb-4">{t('footer.contact')}</h4>

            <div className="space-y-3 text-white/85">
              <div className="flex gap-3">
                <div className="mt-0.5">
                  <FiMapPin />
                </div>
                <div className="leading-snug">{contact.address}</div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="mt-0.5">
                  <FiPhone />
                </div>
                <div className="leading-snug">
                  <a href={`tel:${contact.phone1}`} className="hover:text-white transition">
                    {contact.phone1}
                  </a>
                  <span className="mx-2 text-white/40">•</span>
                  <a href={`tel:${contact.phone2}`} className="hover:text-white transition">
                    {contact.phone2}
                  </a>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="mt-0.5">
                  <FiMail />
                </div>
                <div className="leading-snug">
                  <a href={`mailto:${contact.email}`} className="hover:text-white transition">
                    {contact.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="mt-0.5">
                  <FiClock />
                </div>
                <div className="leading-snug">{contact.hours}</div>
              </div>

              <div className="pt-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center w-full px-3 py-2 rounded-xl
                             bg-white/10 hover:bg-white/15 transition font-semibold"
                >
                  {t('footer.getInTouch')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* LEGAL ROW */}
        <div className="mt-10 pt-6 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/80">
            <div>
              © {year} <b className="text-white">Maxula.ma</b> — {t('footer.rights')}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/cgv" className="hover:text-white transition">
                {t('footer.cgv')}
              </Link>
              <Link to="/terms" className="hover:text-white transition">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="hover:text-white transition">
                {t('footer.privacy')}
              </Link>
              <Link to="/legal" className="hover:text-white transition">
                {t('footer.legal')}
              </Link>
            </div>
          </div>

          <div className="mt-3 text-xs text-white/60 leading-relaxed">
            {t('footer.intermediary')}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
